import ReactModal from 'react-modal'
import { ClassNames } from '@emotion/react'
import { InterpolationPrimitive } from '@emotion/serialize'
import { ReactElement, ReactNode, useCallback, useRef, useState } from 'react'
import {
  SFocusLock,
  SModal,
  SModalBottomBar,
  SModalContent,
  SModalTopBar,
} from './FullScreenView.styled'
import { useIsUserIdle, useLatestValueRef } from 'utils/hooks'
import { Button } from 'components/button/Button'
import { Icon } from 'components/icon'

export interface FullScreenViewConfig {
  topBarContent?: ReactElement
  bottomBarContent?: ReactElement
  disableFocusLock?: boolean
  modalContentCss?: InterpolationPrimitive
  modalCss?: InterpolationPrimitive
  onClose?: () => void
}

interface IModalRender {
  (params: { isOpen: boolean; close: () => void }): ReactNode
}

export const FullScreenView = (props: {
  isOpen: boolean
  onUserClosesModal: () => void
  onRequestClose: () => void
  render: IModalRender
  disableFocusLock?: boolean
  modalContentCss?: InterpolationPrimitive
  modalCss?: InterpolationPrimitive
  topBarContent?: ReactElement
  bottomBarContent?: ReactElement
}) => {
  const isIdle = useIsUserIdle(5000)

  return (
    <ClassNames>
      {({ css }) => (
        <ReactModal
          isOpen={props.isOpen}
          onRequestClose={props.onRequestClose}
          shouldCloseOnOverlayClick={true}
          ariaHideApp={false}
          overlayClassName={css`
            z-index: 6;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            height: 100%;
            width: 100%;
            background: black;
            overflow: hidden;

            display: flex;
            flex-direction: column;
          `}
          className={css`
            transform: none;
            border-radius: 0;
            outline: none;
            height: 100%;
            width: 100%;
          `}
        >
          <SFocusLock disabled={props.disableFocusLock}>
            <SModal css={props.modalCss}>
              <SModalTopBar css={{ opacity: isIdle ? '0%' : '100%' }}>
                <Button type="button" variant="text" onClick={props.onUserClosesModal}>
                  <Icon name="ArrowLeft" color="white" />
                </Button>
                {props.topBarContent}
              </SModalTopBar>
              <SModalContent css={props.modalContentCss}>
                {props.render({
                  isOpen: props.isOpen,
                  close: props.onUserClosesModal,
                })}
              </SModalContent>
              <SModalBottomBar css={{ opacity: isIdle ? '0%' : '100%' }}>
                {props.bottomBarContent}
              </SModalBottomBar>
            </SModal>
          </SFocusLock>
        </ReactModal>
      )}
    </ClassNames>
  )
}

export const useFullScreenView = (render: IModalRender, config?: FullScreenViewConfig) => {
  const [isOpen, setIsOpen] = useState(false)
  const closeCallback = useRef<(() => void) | null>(null)

  const configRef = useLatestValueRef(config)

  const openModal: () => Promise<void> = useCallback(
    () =>
      new Promise((resolve) => {
        setIsOpen(true)
        closeCallback.current = resolve
      }),
    []
  )

  const closeModal = useCallback(() => {
    if (closeCallback.current) {
      closeCallback.current()
    }
    setIsOpen(false)
  }, [])

  const onUserClosesModal = useCallback(() => {
    if (configRef.current?.onClose) configRef.current.onClose()
    closeModal()
  }, [closeModal, configRef])

  const modalLayout = (
    <FullScreenView
      isOpen={isOpen}
      onUserClosesModal={onUserClosesModal}
      onRequestClose={closeModal}
      render={render}
      disableFocusLock={config?.disableFocusLock}
      modalContentCss={config?.modalContentCss}
      modalCss={config?.modalCss}
      topBarContent={config?.topBarContent}
      bottomBarContent={config?.bottomBarContent}
    />
  )

  return [modalLayout, openModal, closeModal, isOpen] as const
}
